import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { AlertService } from '@context/frontend/alert';
import { FIREBASE_AUTH, ORGANIZATION_ID } from '@context/frontend/common';
import { ExternalAuthService } from '../services';

@Injectable({ providedIn: 'root' })
export class ExternalShareAuthGuard implements CanActivate {
	private readonly auth = inject(FIREBASE_AUTH);
	private readonly organizationId$ = inject(ORGANIZATION_ID);
	private readonly router = inject(Router);
	private readonly alert = inject(AlertService);
	private readonly externalAuthService = inject(ExternalAuthService);

	async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
		const orgId = route.paramMap.get('org');
		const externalId = route.paramMap.get('id');
		if (!orgId || !externalId) return this.redirect();

		this.organizationId$.next(orgId);

		try {
			const authenticated =
				await this.externalAuthService.authenticateForShare(
					orgId,
					externalId,
				);
			if (!authenticated) return this.redirect();

			const user = this.auth.currentUser;
			if (!user) return this.redirect();

			const validSession =
				await this.externalAuthService.setupExternalSession(
					user.uid,
					orgId,
					externalId,
				);
			if (!validSession) return this.redirect();

			return true;
		} catch {
			return this.redirect();
		}
	}

	private async redirect(): Promise<boolean> {
		await this.router.navigate(['/']);
		this.alert.open('external-content-not-found').subscribe();
		return false;
	}
}
